/**
 * Keep the document's web app manifest + iOS title aligned with the surface
 * the user is currently on, so "Add to Home Screen" / shortcuts open the
 * same product they saved from (home vs Mini Website vs CRM).
 */
export type WebAppSurface = 'home' | 'miniwebsite' | 'miniwebsite-host' | 'crm';

export function resolveWebAppSurface(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  pathname = typeof window !== 'undefined' ? window.location.pathname : '/',
): WebAppSurface {
  const host = String(hostname || '').toLowerCase();
  const path = String(pathname || '/').replace(/\/+$/, '') || '/';

  if (/^(?:www\.)?miniwebsite\.tcards\.org$/.test(host)) {
    return 'miniwebsite-host';
  }
  if (
    path === '/miniwebsite' ||
    path.startsWith('/miniwebsite/') ||
    path === '/website' ||
    path.startsWith('/website/')
  ) {
    return 'miniwebsite';
  }
  if (
    path === '/crm' ||
    path.startsWith('/crm/') ||
    path === '/crm-login' ||
    path.startsWith('/crm-login/') ||
    path === '/sales-crm' ||
    path.startsWith('/sales-crm/') ||
    path === '/field-team-management' ||
    path === '/field-operations' ||
    path === '/sales-team-tracking' ||
    path === '/field-service-management' ||
    path === '/technician-tracking' ||
    path === '/security-workforce-management' ||
    path === '/supervisor-management' ||
    path === '/cleaning-workforce-management' ||
    path === '/field-workforce-management' ||
    path === '/crm-lead-management' ||
    path === '/crm-contact'
  ) {
    return 'crm';
  }
  return 'home';
}

export function manifestHrefForSurface(surface: WebAppSurface): string {
  switch (surface) {
    case 'miniwebsite-host':
      return '/manifest-miniwebsite-host.json';
    case 'miniwebsite':
      return '/manifest-miniwebsite.json';
    case 'crm':
      return '/manifest-crm.json';
    default:
      return '/manifest.json';
  }
}

export function appleTitleForSurface(surface: WebAppSurface): string {
  switch (surface) {
    case 'miniwebsite-host':
    case 'miniwebsite':
      return 'Tcards Mini Website';
    case 'crm':
      return 'Tcards CRM';
    default:
      return 'Tcards';
  }
}

export function syncWebAppManifest(opts?: {
  hostname?: string;
  pathname?: string;
}): WebAppSurface {
  if (typeof document === 'undefined') return 'home';
  const surface = resolveWebAppSurface(opts?.hostname, opts?.pathname);
  const href = manifestHrefForSurface(surface);
  const title = appleTitleForSurface(surface);

  let link = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }

  let apple = document.querySelector(
    'meta[name="apple-mobile-web-app-title"]',
  ) as HTMLMetaElement | null;
  if (!apple) {
    apple = document.createElement('meta');
    apple.name = 'apple-mobile-web-app-title';
    document.head.appendChild(apple);
  }
  if (apple.content !== title) {
    apple.content = title;
  }

  return surface;
}
